import { useMutation } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { object, string } from "yup";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { FunnelTextField } from "../../FunnelTextField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { FunnelButton } from "../../FunnelButton";
import { FunnelBackButton } from "../../FunnelBackButton";
import { ValidatedFormikField } from "@portal/app/_components/ui-elements/formHelpers";
import { useAuthenticatedContext } from "@portal/app/_components/context/AuthenticatedContext";
import { ClientFacingErrors } from "@portal/app/_common/types/clientErrors";
import { detectKnownClientError } from "@portal/app/_common/lib/clientErrorUtils";
import { cleanPhoneNumber, renderPhoneNumber } from "@portal/app/_common/lib/formatRenderers";
import { yupRules } from "@portal/app/_common/lib/yupValidationRules";
import { captureException } from "@sentry/nextjs";
import { logger } from "@portal/app/_common/lib/logger";
import { SignInInsteadDialog } from "@portal/app/login/_components/SignInInsteadDialog";
import {
  RequestPhoneCodeHostedDocument,
  VerifyPhoneCodeHostedDocument,
  UpdateCustomerHostedResultDocument,
} from "../generated";
import { useFormFunnelTracking } from "../../hooks/useFormFunnelTracking";
import { QuestionDescription } from "../../QuestionDescription";
import { PhoneStepProps } from "./types";
import { useHostedStepAction } from "./useHostedStepAction";
import { useFormFunnelHeadingLevel } from "../../FormFunnelHeadingContext";

export const PhoneStep = ({
  user,
  customerDataRef,
  refetchViewer,
  onNext,
  onBack,
  onProgressStageChange,
  description,
}: PhoneStepProps) => {
  const headingLevel = useFormFunnelHeadingLevel();
  const descriptionText = description?.content ? (
    <Box sx={{ my: 1 }}>
      <QuestionDescription content={description.content} testId="hosted-phone-step-description" />
    </Box>
  ) : null;
  const { openSignInModal, customerId } = useAuthenticatedContext();
  const { trackEvent } = useFormFunnelTracking();
  const [signInInsteadDialogOpen, setSignInInsteadDialogOpen] = useState(false);
  const action = useHostedStepAction(customerId ?? "phone");

  // When the user signs in as a different user via the modal, customerId changes.
  // Refetch the viewer so PhoneStep re-renders with the new user's verified phone.
  const prevCustomerIdRef = useRef(customerId);
  useEffect(() => {
    if (customerId !== prevCustomerIdRef.current) {
      prevCustomerIdRef.current = customerId;
      void refetchViewer().catch(logger.error);
    }
  }, [customerId, refetchViewer]);
  const [codeSent, setCodeSent] = useState(false);
  const [phone, setPhone] = useState(user?.phone || "");
  const [resendCountdown, setResendCountdown] = useState(30);
  const [resending, setResending] = useState(false);
  const isVerified = user?.phoneVerified;
  const progressStage = codeSent || isVerified ? "code" : "phone";

  useEffect(() => {
    onProgressStageChange?.(progressStage);
  }, [progressStage, onProgressStageChange]);

  useEffect(() => {
    if (!codeSent || resendCountdown <= 0) return;
    const timer = setTimeout(() => {
      setResendCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [codeSent, resendCountdown]);

  const [updateCustomer] = useMutation(UpdateCustomerHostedResultDocument);
  const [requestPhoneCode] = useMutation(RequestPhoneCodeHostedDocument);
  const [verifyPhoneCode] = useMutation(VerifyPhoneCodeHostedDocument);

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    try {
      await requestPhoneCode({ variables: { phone, isMobile: true } });
      setResendCountdown(30);
    } catch (e) {
      const clientError = detectKnownClientError(e);
      // Defensive: requestPhoneCode doesn't currently throw this, but guard in case server behavior changes
      if (clientError === ClientFacingErrors.UserWithPhoneAlreadyExists) {
        setSignInInsteadDialogOpen(true);
        return;
      }
      logger.error(e);
      captureException(e);
    } finally {
      setResending(false);
    }
  };

  if (isVerified) {
    // Save verified phone and auto-advance
    const verifiedPhone = user?.phone;
    if (verifiedPhone) {
      customerDataRef.current.phone = verifiedPhone;
    }
    return (
      <Box>
        {onBack && (
          <FunnelBackButton appearance="text" onClick={onBack} sx={{ mb: 1 }}>
            Back
          </FunnelBackButton>
        )}
        <Typography
          component={headingLevel}
          sx={{
            color: "text.primary",
            fontSize: "1.5rem",
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: "-0.01em",
            mb: 2,
          }}
        >
          Phone Verified
        </Typography>
        {description?.placement === "above" && descriptionText}
        <Box sx={{ textAlign: "center" }}>
          <FontAwesomeIcon icon={faCheckCircle} size="3x" color="green" />
        </Box>
        <Typography sx={{ mt: 2, mb: 2, textAlign: "center" }}>
          Your phone number is verified.
        </Typography>
        {description?.placement !== "above" && descriptionText}
        <FunnelButton
          onClick={() => {
            const generation = action.begin();
            if (generation !== undefined && action.complete(generation)) onNext();
          }}
          sx={{ maxWidth: "none", minHeight: 56, mt: 1, fontSize: "1.1rem" }}
        >
          Continue
        </FunnelButton>
      </Box>
    );
  }

  return (
    <>
      <Formik
        initialValues={{ phone: renderPhoneNumber(phone), code: "" }}
        validationSchema={object({
          phone: yupRules.required.phone.transform((value) => value?.replace(/\D/g, "") ?? ""),
          code: codeSent ? string().length(4, "Must be 4 digits").required("Required") : string(),
        })}
        onSubmit={async (values, { setSubmitting, setFieldError }) => {
          const generation = action.begin();
          if (generation === undefined) return;
          try {
            if (!codeSent) {
              // Send code
              const clean = cleanPhoneNumber(values.phone);
              await updateCustomer({ variables: { data: { phone: clean } } }); // Save phone first
              await requestPhoneCode({ variables: { phone: clean, isMobile: true } });
              if (!action.isCurrent(generation)) return;
              setPhone(clean);
              setCodeSent(true);
              setResendCountdown(30);
            } else {
              // Verify code
              const verification = await verifyPhoneCode({
                variables: { phone, code: values.code },
              });
              if (!action.isCurrent(generation)) return;
              if (verification.data?.verifyPhoneCode?.phoneVerified !== true) {
                setFieldError("code", "Invalid code. Please try again.");
                return;
              }
              // Save verified phone to customer data
              customerDataRef.current.phone = phone;
              await refetchViewer();
              if (!action.complete(generation)) return;
              trackEvent("Form Funnel - Hosted Step Completed", { stepType: "phone" });
              onNext();
            }
          } catch (e) {
            if (!codeSent) {
              const clientError = detectKnownClientError(e);
              if (clientError === ClientFacingErrors.UserWithPhoneAlreadyExists) {
                setSignInInsteadDialogOpen(true);
                return;
              }
            }
            logger.error(e);
            if (!detectKnownClientError(e)) {
              captureException(e);
            }
            if (!codeSent) {
              setFieldError("phone", "Could not send code. Please try again.");
            } else {
              setFieldError("code", "Invalid code. Please try again.");
            }
          } finally {
            action.finish(generation);
            setSubmitting(false);
          }
        }}
      >
        {({ handleSubmit, isSubmitting, isValid, setFieldValue }) => (
          <form onSubmit={handleSubmit} data-testid="hosted-phone-step">
            {onBack && (
              <FunnelBackButton appearance="text" onClick={onBack} sx={{ mb: 1 }}>
                Back
              </FunnelBackButton>
            )}
            <Typography
              component={headingLevel}
              sx={{
                color: "text.primary",
                fontSize: "1.5rem",
                fontWeight: 700,
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
                mb: 1,
              }}
            >
              Verify your phone
            </Typography>
            {description?.placement === "above" && descriptionText}

            {codeSent && (
              <Typography
                color="text.secondary"
                sx={{ mb: 2, lineHeight: 1.5 }}
                data-testid="hosted-phone-code-sent-to"
              >
                We sent a code to {renderPhoneNumber(phone)}
              </Typography>
            )}

            <ValidatedFormikField
              name="phone"
              label="Mobile phone"
              as={FunnelTextField}
              fullWidth
              disabled={codeSent}
              autoComplete="tel"
              placeholder="(___) ___-____"
              inputProps={{ inputMode: "tel", maxLength: 14 }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const digits = cleanPhoneNumber(e.target.value) ?? "";
                void setFieldValue("phone", renderPhoneNumber(digits));
              }}
              sx={{ mb: codeSent ? 2.25 : 0 }}
            />

            {codeSent && (
              <>
                <ValidatedFormikField
                  name="code"
                  label="Verification code"
                  as={FunnelTextField}
                  fullWidth
                  autoComplete="one-time-code"
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 4, inputMode: "numeric" }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    textAlign: "center",
                    ...(resendCountdown === 0 && !resending
                      ? {
                          color: "primary.main",
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }
                      : { color: "text.secondary" }),
                  }}
                  {...(resendCountdown === 0 && !resending
                    ? {
                        role: "button",
                        tabIndex: 0,
                        onClick: handleResend,
                        onKeyDown: (e: React.KeyboardEvent) => {
                          if (e.key === "Enter" || e.key === " ") void handleResend();
                        },
                      }
                    : {})}
                >
                  {resending
                    ? "Resending..."
                    : resendCountdown > 0
                      ? `Resend code in ${resendCountdown}s`
                      : "Didn\u2019t receive a code? Resend"}
                </Typography>
              </>
            )}

            {description?.placement !== "above" && descriptionText}
            <FunnelButton
              type="submit"
              disabled={!isValid || isSubmitting}
              sx={{ maxWidth: "none", minHeight: 56, mt: 3, fontSize: "1.1rem" }}
            >
              {isSubmitting
                ? codeSent
                  ? "Verifying..."
                  : "Sending..."
                : codeSent
                  ? "Verify code"
                  : "Send code"}
            </FunnelButton>
          </form>
        )}
      </Formik>
      <SignInInsteadDialog
        onClose={(shouldOpenSignIn) => {
          setSignInInsteadDialogOpen(false);
          if (shouldOpenSignIn) {
            openSignInModal("SIGN_IN");
          }
        }}
        open={signInInsteadDialogOpen}
        isDreamSearch
      />
    </>
  );
};
